<template>
  <el-dialog
    title="选择摄像头"
    :visible.sync="dialogVisible"
    width="600px"
    :before-close="handleClose"
    :close-on-click-modal="false"
    class="camera-selection-dialog"
  >
    <div class="dialog-content">
      <div class="bridge-info mb-4 p-3 bg-blue-50 rounded-lg">
        <h4 class="text-lg font-semibold text-blue-800 mb-2">
          <i class="el-icon-location text-blue-600 mr-2"></i>
          {{ bridgeName }}
        </h4>
        <p class="text-sm text-blue-600">
          发现 {{ cameras.length }} 个可用的监控设备，请选择：
        </p>
      </div>

      <div class="camera-list">
        <div 
          v-for="(camera, index) in cameras" 
          :key="camera.id || index"
          class="camera-item flex items-center justify-between p-4 border border-gray-200 rounded-lg mb-3 hover:border-blue-400 hover:bg-blue-50 transition-all cursor-pointer"
          :class="{ 'border-blue-500 bg-blue-50': selectedCameraIndex === index }"
          @click="selectCamera(index)"
        >
          <div class="camera-info flex-1">
            <div class="camera-name flex items-center mb-2">
              <i class="el-icon-video-camera text-blue-500 mr-2 text-lg"></i>
              <span class="font-medium text-gray-800">{{ camera.name || `摄像头 ${index + 1}` }}</span>
            </div>
            
            <div class="camera-details flex flex-wrap gap-4 text-sm text-gray-600">
              <div class="detail-item flex items-center">
                <i class="el-icon-connection text-gray-400 mr-1"></i>
                <span class="font-mono">{{ camera.host || 'N/A' }}</span>
              </div>
              
              <div v-if="camera.port" class="detail-item flex items-center">
                <span class="font-mono">端口: {{ camera.port }}</span>
              </div>
                            
              <div v-if="camera.enabled !== undefined" class="detail-item flex items-center">
                <i :class="camera.enabled === 1 ? 'el-icon-check text-green-500' : 'el-icon-close text-red-500'" class="mr-1"></i>
                <span :class="camera.enabled === 1 ? 'text-green-600' : 'text-red-600'">
                  {{ camera.enabled === 1 ? '已启用' : '已禁用' }}
                </span>
              </div>
            </div>
          </div>
          
          <div class="selection-indicator ml-4">
            <i 
              v-if="selectedCameraIndex === index" 
              class="el-icon-success text-blue-500 text-xl"
            ></i>
            <i 
              v-else 
              class="el-icon-circle-check text-gray-300 text-xl"
            ></i>
          </div>
        </div>
      </div>
    </div>

    <div slot="footer" class="dialog-footer flex justify-between">
      <div class="flex-1"  style="display: none;">
        <el-tooltip content="选择第一个摄像头作为默认选项" placement="top">
          <el-button @click="selectCamera(0)" size="small" type="text">
            <i class="el-icon-top mr-1"></i>选择第一个
          </el-button>
        </el-tooltip>
      </div>
      
      <div class="button-group">
        <el-button @click="handleClose" size="medium">取消</el-button>
        <el-button 
          type="primary" 
          @click="confirmSelection" 
          size="medium"
          :disabled="selectedCameraIndex === -1"
        >
          <i class="el-icon-video-camera mr-1"></i>
          打开视频监控
        </el-button>
      </div>
    </div>
  </el-dialog>
</template>

<script>
export default {
  name: 'CameraSelectionDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    cameras: {
      type: Array,
      default: () => []
    },
    bridgeName: {
      type: String,
      default: '未知桥梁'
    }
  },
  data() {
    return {
      selectedCameraIndex: -1
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible
      },
      set(value) {
        this.$emit('update:visible', value)
      }
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        // 弹窗打开时重置选择
        this.selectedCameraIndex = -1
        // 如果只有一个摄像头，自动选中
        if (this.cameras.length === 1) {
          this.selectedCameraIndex = 0
        }
      }
    },
    cameras: {
      handler(newCameras) {
        // 摄像头列表变化时重置选择
        this.selectedCameraIndex = -1
        // 如果只有一个摄像头，自动选中
        if (newCameras && newCameras.length === 1) {
          this.selectedCameraIndex = 0
        }
      },
      immediate: true
    }
  },
  methods: {
    selectCamera(index) {
      if (index >= 0 && index < this.cameras.length) {
        this.selectedCameraIndex = index
      }
    },
    
    confirmSelection() {
      if (this.selectedCameraIndex >= 0 && this.selectedCameraIndex < this.cameras.length) {
        const selectedCamera = this.cameras[this.selectedCameraIndex]
        console.log('用户选择了摄像头:', selectedCamera)
        
        // 发送选择事件
        this.$emit('camera-selected', {
          camera: selectedCamera,
          index: this.selectedCameraIndex
        })
        
        // 关闭弹窗
        this.handleClose()
      } else {
        this.$message.warning('请先选择一个摄像头')
      }
    },
    
    handleClose() {
      this.selectedCameraIndex = -1
      this.$emit('close')
      this.$emit('update:visible', false)
    }
  }
}
</script>

<style scoped>
.camera-selection-dialog {
  /* 确保弹窗在最上层 */
  z-index: 9999;
}

.camera-selection-dialog .el-dialog {
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
}

.camera-selection-dialog .el-dialog__header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 12px 12px 0 0;
  padding: 20px 24px 16px;
}

.camera-selection-dialog .el-dialog__title {
  color: white;
  font-size: 18px;
  font-weight: 600;
}

.camera-selection-dialog .el-dialog__headerbtn .el-dialog__close {
  color: white;
  font-size: 18px;
}

.camera-selection-dialog .el-dialog__headerbtn .el-dialog__close:hover {
  color: #f0f0f0;
}

.dialog-content {
  max-height: 60vh;
  overflow-y: auto;
}

.bridge-info {
  border-left: 4px solid #3b82f6;
}

.camera-list {
  max-height: 400px;
  overflow-y: auto;
}

.camera-item {
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.camera-item:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.camera-item.border-blue-500 {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.camera-name {
  font-size: 16px;
}

.camera-details {
  font-size: 13px;
}

.detail-item {
  min-width: 0; /* 防止flex项目溢出 */
}

.detail-item span {
  word-break: break-all; /* 长文本自动换行 */
}

.selection-indicator {
  flex-shrink: 0;
}

.dialog-footer {
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
}

.button-group {
  display: flex;
  gap: 12px;
}

/* 自定义滚动条样式 */
.camera-list::-webkit-scrollbar,
.dialog-content::-webkit-scrollbar {
  width: 6px;
}

.camera-list::-webkit-scrollbar-track,
.dialog-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.camera-list::-webkit-scrollbar-thumb,
.dialog-content::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.camera-list::-webkit-scrollbar-thumb:hover,
.dialog-content::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .camera-selection-dialog .el-dialog {
    width: 95% !important;
    margin: 5vh auto 0;
  }
  
  .camera-details {
    flex-direction: column;
    gap: 8px;
  }
  
  .dialog-footer {
    flex-direction: column;
    gap: 12px;
  }
  
  .button-group {
    justify-content: center;
  }
}
</style>
